import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ToastType = 'error' | 'success' | 'info' | 'warning';

export interface ToastAction {
  /** 클릭 시 이동할 경로 (navigate 사용) */
  navigateTo?: string;
  /** 클릭 시 실행할 콜백 */
  onClick?: () => void;
  /** 액션 버튼 라벨 (없으면 전체 토스트 클릭) */
  label?: string;
}

/** 토스트 배지 정보 */
export interface ToastBadges {
  /** 조직명 */
  orgName?: string;
  /** 현장명 */
  siteName?: string;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  expiresAt: number; // 만료 시간 (timestamp)
  action?: ToastAction;
  badges?: ToastBadges;
}

interface ToastState {
  toasts: Toast[];
  showToast: (message: string, type?: ToastType, action?: ToastAction, badges?: ToastBadges) => void;
  hideToast: (id: string) => void;
  clearToasts: () => void;
}

const TOAST_DURATION = 4000; // 4초

export const useToastStore = create<ToastState>()(
  persist(
    (set) => ({
      toasts: [],

      showToast: (message: string, type: ToastType = 'info', action?: ToastAction, badges?: ToastBadges) => {
        const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // 클릭 액션이 있는 경우 더 오래 표시 (6초)
        const duration = action ? 6000 : TOAST_DURATION;
        const expiresAt = Date.now() + duration;

        set((state) => {
          // 만료된 토스트 정리 + 새 토스트 추가
          const now = Date.now();
          const activeToasts = state.toasts.filter((t) => t.expiresAt > now);
          return {
            toasts: [...activeToasts, { id, message, type, expiresAt, action, badges }],
          };
        });

        // 자동 제거
        setTimeout(() => {
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
          }));
        }, duration);
      },

      hideToast: (id: string) => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      },

      clearToasts: () => {
        set({ toasts: [] });
      },
    }),
    {
      name: 'toast-storage',
      storage: createJSONStorage(() => sessionStorage),
      // 복원 시 만료된 토스트 정리
      onRehydrateStorage: () => (state) => {
        if (state) {
          const now = Date.now();
          state.toasts = state.toasts.filter((t) => t.expiresAt > now);

          // 남은 토스트에 대해 타이머 재설정
          state.toasts.forEach((toast) => {
            const remaining = toast.expiresAt - now;
            if (remaining > 0) {
              setTimeout(() => {
                useToastStore.setState((s) => ({
                  toasts: s.toasts.filter((t) => t.id !== toast.id),
                }));
              }, remaining);
            }
          });
        }
      },
    }
  )
);
